import React, { useState, useEffect } from 'react'
    import { motion, AnimatePresence } from 'framer-motion'
    import * as FiIcons from 'react-icons/fi'
    import SafeIcon from '../common/SafeIcon'
    import PhotoModal from './PhotoModal'
    import {
      getBaseRelationshipType,
      getOppositeRelationship,
      getRelationshipBasedOnSex,
    } from '../utils/relationshipMappings'

    const {
      FiEdit,
      FiTrash2,
      FiUsers,
      FiHeart,
      FiHome,
      FiBriefcase,
      FiChevronDown,
      FiChevronUp,
      FiUser,
      FiMapPin,
    } = FiIcons

    const PersonCard = ({
      person,
      relationships = [],
      people = [],
      onEdit,
      onDelete,
      onViewRelationships,
      onNavigateToPerson,
    }) => {
      const [showRelationships, setShowRelationships] = useState(false)
      const [imageError, setImageError] = useState(false)
      const [showPhotoModal, setShowPhotoModal] = useState(false)
      const proximityValue = person.proximity || 'Medium'

      const getSexIcon = (sex) => {
        switch (sex) {
          case 'male':
            return '♂️'
          case 'female':
            return '♀️'
          default:
            return '⚧️'
        }
      }

      const getProximityColor = (proximity) => {
        switch ((proximity || '').toLowerCase()) {
          case 'close':
            return 'bg-green-100 text-green-700 border-green-200'
          case 'medium':
            return 'bg-yellow-100 text-yellow-700 border-yellow-200'
          case 'far':
            return 'bg-blue-100 text-blue-700 border-blue-200'
          default:
            return 'bg-gray-100 text-gray-700 border-gray-200'
        }
      }

      const getFamilyIcon = (relationshipType) => {
        const familyTypes = [
          'father',
          'mother',
          'son',
          'daughter',
          'brother',
          'sister',
          'grandfather',
          'grandmother',
          'grandson',
          'granddaughter',
          'uncle',
          'aunt',
          'nephew',
          'niece',
          'husband',
          'wife',
          'ex-husband',
          'ex-wife',
          // Extended blood relatives
          'great-grandfather',
          'great-grandmother',
          'great-grandson',
          'great-granddaughter',
          'great-great-grandfather',
          'great-great-grandmother',
          'great-uncle',
          'great-aunt',
          'grandnephew',
          'grandniece',
          'cousin',
          'male cousin',
          'female cousin',
          'second_cousin',
          'male 2nd cousin',
          'female 2nd cousin',
          'third_cousin',
          'male 3rd cousin',
          'female 3rd cousin',
        ]
        const friendTypes = ['friend']
        const workTypes = ['colleague', 'boss', 'employee']

        if (familyTypes.includes(relationshipType)) return FiHome
        if (friendTypes.includes(relationshipType)) return FiHeart
        if (workTypes.includes(relationshipType)) return FiBriefcase
        return FiUsers
      }

      // Get relationship details with correct perspective and sort them
      const getRelationshipLabel = (
        rel,
        isPersonA,
        relatedPersonSex,
        fallback
      ) => {
        const normalizedSex = String(relatedPersonSex || 'other')
          .trim()
          .toLowerCase()
        if (isPersonA) {
          const baseTypeAToB = getBaseRelationshipType(rel.relationship_type)
          if (baseTypeAToB) {
            const expectedBaseForBToA = getOppositeRelationship(
              baseTypeAToB,
              null
            )
            if (expectedBaseForBToA) {
              return (
                getRelationshipBasedOnSex(
                  expectedBaseForBToA,
                  normalizedSex
                ) || fallback
              )
            }
          }
        } else {
          const baseTypeBToA = getBaseRelationshipType(rel.relationship_type_b)
          if (baseTypeBToA) {
            const expectedBaseForAToB = getOppositeRelationship(
              baseTypeBToA,
              null
            )
            if (expectedBaseForAToB) {
              return (
                getRelationshipBasedOnSex(
                  expectedBaseForAToB,
                  normalizedSex
                ) || fallback
              )
            }
          }
        }
        return fallback
      }

      const getRelationshipDetails = () => {
        const details = relationships.map((rel) => {
          const isPersonA =
            rel.person_a?.id === person.id || rel.person_a_id === person.id
          const relatedPersonId = isPersonA
            ? rel.person_b?.id || rel.person_b_id
            : rel.person_a?.id || rel.person_a_id
          const relatedPersonName = isPersonA
            ? rel.person_b?.name
            : rel.person_a?.name
          const relatedPersonFromRel = isPersonA ? rel.person_b : rel.person_a
          const relatedPerson =
            relatedPersonFromRel || people.find((p) => p.id === relatedPersonId)
          const personName = relatedPerson?.name || relatedPersonName || 'Unknown'
          // Get the correct relationship type from the current person's perspective
          const relatedPersonSex =
            relatedPerson?.sex || (isPersonA ? rel.person_b?.sex : rel.person_a?.sex)
          const fallbackRelationshipType = isPersonA
            ? rel.relationship_type_b || rel.relationship_type
            : rel.relationship_type || rel.relationship_type_b
          const relationshipType = getRelationshipLabel(
            rel,
            isPersonA,
            relatedPersonSex,
            fallbackRelationshipType || 'Unknown'
          )
          return {
            relationshipId: rel.id, // Pass unique relationship ID for key
            name: personName,
            type: relationshipType,
            icon: getFamilyIcon(relationshipType),
            id: relatedPersonId,
          }
        })
        // Sort alphabetically by name
        return details.sort((a, b) => a.name.localeCompare(b.name))
      }

      const relationshipDetails = getRelationshipDetails()
      const relationshipCount = relationshipDetails.length

      // Toggle this card's relationship display only
      const toggleRelationships = (e) => {
        e.stopPropagation() // Prevent event bubbling
        setShowRelationships(!showRelationships)
      }

      const handleImageError = () => {
        setImageError(true)
      }

      const handlePhotoClick = (e) => {
        e.stopPropagation() // Prevent event bubbling
        if (person.photo_url && !imageError) {
          setShowPhotoModal(true)
        }
      }

      // Function to handle clicking on a relationship item
      const handleRelationshipClick = (relatedPersonId, e) => {
        e.stopPropagation()
        if (onNavigateToPerson) {
          onNavigateToPerson(relatedPersonId)
        }
      }

      const renderPhoto = () => {
        if (person.photo_url && !imageError) {
          return (
            <div
              className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-200 cursor-pointer hover:border-blue-400 transition-colors group"
              onClick={handlePhotoClick}
              title="Click to view full size"
            >
              <img
                src={person.photo_url}
                alt={person.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                onError={handleImageError}
              />
            </div>
          )
        }
        return (
          <div className="w-12 h-12 rounded-full bg-gray-100 border-2 border-gray-200 flex items-center justify-center">
            <SafeIcon icon={FiUser} className="text-gray-400 text-xl" />
          </div>
        )
      }

      return (
        <>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -2 }}
            className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow"
            id={`person-${person.id}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                {renderPhoto()}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                    <span className="text-sm">{getSexIcon(person.sex)}</span>
                    <span className="truncate">{person.name}</span>
                  </h3>
                  {/* Proximity Badge and Family Badge */}
                  <div className="flex items-center mt-1 space-x-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border flex items-center ${getProximityColor(
                        proximityValue
                      )}`}
                    >
                      <SafeIcon icon={FiMapPin} className="mr-1" size={10} />
                      <span className="capitalize">{proximityValue}</span>
                    </span>
                    {person.is_family && (
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full flex items-center">
                        <SafeIcon icon={FiHome} className="mr-1" size={10} />
                        Family
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex space-x-2 flex-shrink-0">
                <button
                  onClick={() => onViewRelationships(person)}
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                  title="View Relationships"
                >
                  <SafeIcon icon={FiUsers} size={18} />
                </button>
                <button
                  onClick={() => onEdit(person)}
                  className="text-green-600 hover:text-green-800 transition-colors"
                  title="Edit"
                >
                  <SafeIcon icon={FiEdit} size={18} />
                </button>
                <button
                  onClick={() => onDelete(person.id)}
                  className="text-red-600 hover:text-red-800 transition-colors"
                  title="Delete"
                >
                  <SafeIcon icon={FiTrash2} size={18} />
                </button>
              </div>
            </div>

            {person.nicknames && (
              <p className="text-sm text-gray-600 mb-3">
                <span className="font-medium">Nicknames:</span> {person.nicknames}
              </p>
            )}

            {relationshipCount > 0 && (
              <div className="mb-2">
                <button
                  onClick={toggleRelationships}
                  className="flex items-center justify-between w-full py-1 px-2 bg-gray-50 hover:bg-gray-100 rounded text-sm transition-colors"
                >
                  <span className="flex items-center">
                    <SafeIcon
                      icon={FiUsers}
                      className="text-gray-500 mr-1"
                      size={14}
                    />
                    <span className="text-gray-700 font-medium">
                      {relationshipCount} Relationship
                      {relationshipCount !== 1 ? 's' : ''}
                    </span>
                  </span>
                  <SafeIcon
                    icon={showRelationships ? FiChevronUp : FiChevronDown}
                    className="text-gray-500"
                    size={16}
                  />
                </button>
                <AnimatePresence>
                  {showRelationships && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-2 pb-1 space-y-1">
                        {relationshipDetails.map((rel) => (
                          <div
                            key={rel.relationshipId}
                            className="flex items-center space-x-2 text-sm pl-2 cursor-pointer hover:bg-blue-50 rounded p-1 transition-colors"
                            onClick={(e) => handleRelationshipClick(rel.id, e)}
                          >
                            <SafeIcon
                              icon={rel.icon}
                              className="text-gray-500 flex-shrink-0"
                              size={14}
                            />
                            <span className="text-gray-700 truncate">
                              <span className="font-medium">{rel.name}</span>
                              <span className="text-gray-500 ml-1">
                                ({rel.type})
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {person.notes && (
              <p className="text-sm text-gray-600 mt-3 pt-3 border-t border-gray-100">
                <span className="font-medium">Notes:</span> {person.notes}
              </p>
            )}
          </motion.div>
          {/* Photo Modal */}
          <AnimatePresence>
            {showPhotoModal && (
              <PhotoModal
                person={person}
                isOpen={showPhotoModal}
                onClose={() => setShowPhotoModal(false)}
              />
            )}
          </AnimatePresence>
        </>
      )
    }

    export default PersonCard